/**
 * Country data and phone formatting — mirrors src/component/phoneNumberInput/PhoneNumberInput.tsx
 */
export interface Country {
  code: string;
  name: string;
  dialCode: string;
  flag: string;
  format?: string;
}

export const COUNTRIES: Country[] = [
  { code: 'US', name: 'United States', dialCode: '+1', flag: '🇺🇸', format: '(XXX) XXX-XXXX' },
  { code: 'GB', name: 'United Kingdom', dialCode: '+44', flag: '🇬🇧', format: 'XXXX XXXXXX' },
  { code: 'CA', name: 'Canada', dialCode: '+1', flag: '🇨🇦', format: '(XXX) XXX-XXXX' },
  { code: 'AU', name: 'Australia', dialCode: '+61', flag: '🇦🇺', format: 'XXXX XXX XXX' },
  { code: 'FR', name: 'France', dialCode: '+33', flag: '🇫🇷', format: 'XX XX XX XX XX' },
  { code: 'DE', name: 'Germany', dialCode: '+49', flag: '🇩🇪', format: 'XXXX XXXXXXX' },
  { code: 'IT', name: 'Italy', dialCode: '+39', flag: '🇮🇹', format: 'XXX XXX XXXX' },
  { code: 'ES', name: 'Spain', dialCode: '+34', flag: '🇪🇸', format: 'XXX XXX XXX' },
  { code: 'MX', name: 'Mexico', dialCode: '+52', flag: '🇲🇽', format: 'XX XXXX XXXX' },
  { code: 'BR', name: 'Brazil', dialCode: '+55', flag: '🇧🇷', format: '(XX) XXXXX-XXXX' },
  { code: 'IN', name: 'India', dialCode: '+91', flag: '🇮🇳', format: 'XXXXX XXXXX' },
  { code: 'CN', name: 'China', dialCode: '+86', flag: '🇨🇳', format: 'XXX XXXX XXXX' },
  { code: 'JP', name: 'Japan', dialCode: '+81', flag: '🇯🇵', format: 'XX XXXX XXXX' },
  { code: 'KR', name: 'South Korea', dialCode: '+82', flag: '🇰🇷', format: 'XX XXXX XXXX' },
  { code: 'NL', name: 'Netherlands', dialCode: '+31', flag: '🇳🇱', format: 'XX XXX XXXX' },
  { code: 'BE', name: 'Belgium', dialCode: '+32', flag: '🇧🇪', format: 'XXXX XX XX' },
  { code: 'IE', name: 'Ireland', dialCode: '+353', flag: '🇮🇪', format: 'XX XXX XXXX' },
  { code: 'NG', name: 'Nigeria', dialCode: '+234', flag: '🇳🇬', format: 'XXX XXX XXXX' },
  { code: 'ZA', name: 'South Africa', dialCode: '+27', flag: '🇿🇦', format: 'XX XXX XXXX' },
  { code: 'AE', name: 'United Arab Emirates', dialCode: '+971', flag: '🇦🇪', format: 'XX XXX XXXX' },
  { code: 'SA', name: 'Saudi Arabia', dialCode: '+966', flag: '🇸🇦', format: 'XX XXX XXXX' },
  { code: 'KE', name: 'Kenya', dialCode: '+254', flag: '🇰🇪', format: 'XXX XXXXXXX' },
  { code: 'GH', name: 'Ghana', dialCode: '+233', flag: '🇬🇭', format: 'XXX XXX XXXX' },
  { code: 'EG', name: 'Egypt', dialCode: '+20', flag: '🇪🇬', format: 'XXXX XXX XXXX' },
  { code: 'PH', name: 'Philippines', dialCode: '+63', flag: '🇵🇭', format: 'XXX XXX XXXX' },
  { code: 'SG', name: 'Singapore', dialCode: '+65', flag: '🇸🇬', format: 'XXXX XXXX' },
  { code: 'MY', name: 'Malaysia', dialCode: '+60', flag: '🇲🇾', format: 'XX-XXX XXXX' },
  { code: 'NZ', name: 'New Zealand', dialCode: '+64', flag: '🇳🇿', format: 'XXXX XXX XXX' },
  { code: 'PL', name: 'Poland', dialCode: '+48', flag: '🇵🇱', format: 'XXX XXX XXX' },
  { code: 'PT', name: 'Portugal', dialCode: '+351', flag: '🇵🇹', format: 'XXX XXX XXX' },
  { code: 'AR', name: 'Argentina', dialCode: '+54', flag: '🇦🇷', format: 'XX XXXX-XXXX' },
  { code: 'CO', name: 'Colombia', dialCode: '+57', flag: '🇨🇴', format: 'XXX XXX XXXX' },
  { code: 'RU', name: 'Russia', dialCode: '+7', flag: '🇷🇺', format: 'XXX XXX-XX-XX' },
  { code: 'TR', name: 'Turkey', dialCode: '+90', flag: '🇹🇷', format: 'XXX XXX XX XX' },
  { code: 'IL', name: 'Israel', dialCode: '+972', flag: '🇮🇱', format: 'XX-XXX-XXXX' },
  { code: 'PK', name: 'Pakistan', dialCode: '+92', flag: '🇵🇰', format: 'XXX-XXXXXXX' },
];

export function getCountryByDialCode(dialCode: string): Country | undefined {
  if (!dialCode?.trim()) return undefined;
  const normalized = dialCode.trim().startsWith('+') ? dialCode.trim() : `+${dialCode.trim()}`;
  return COUNTRIES.find((c) => c.dialCode === normalized);
}

/** Strip country dial code from full international number; returns national digits only. */
export function getNationalNumberFromFull(fullNumber: string, dialCode: string): string {
  const fullDigits = (fullNumber ?? '').replace(/\D/g, '');
  const codeDigits = (dialCode ?? '').replace(/\D/g, '');
  if (!codeDigits || !fullDigits.startsWith(codeDigits)) return fullDigits;
  return fullDigits.slice(codeDigits.length);
}

/** Parse full phone to extract country and national digits. */
export function parseFullPhone(full: string): { country: Country; nationalDigits: string } {
  const us = COUNTRIES[0];
  if (!full?.trim()) return { country: us, nationalDigits: '' };
  const digits = full.replace(/\D/g, '');
  // Try matching dial codes from longest to shortest
  const sorted = [...COUNTRIES].sort((a, b) => b.dialCode.length - a.dialCode.length);
  for (const c of sorted) {
    const codeDigits = c.dialCode.replace(/\D/g, '');
    if (digits.startsWith(codeDigits)) {
      return { country: c, nationalDigits: digits.slice(codeDigits.length) };
    }
  }
  return { country: us, nationalDigits: digits };
}

export function formatPhoneNumber(rawValue: string, mask?: string): string {
  if (!rawValue) return rawValue;
  const digits = rawValue.replace(/\D/g, '');
  if (!digits) return '';
  if (!mask) return digits;
  let formatted = '';
  let digitIndex = 0;
  for (let i = 0; i < mask.length && digitIndex < digits.length; i++) {
    if (mask[i] === 'X') {
      formatted += digits[digitIndex];
      digitIndex++;
    } else {
      formatted += mask[i];
    }
  }
  if (digitIndex < digits.length) {
    formatted += digits.slice(digitIndex);
  }
  return formatted;
}
